import { useEffect, useState } from "react";
import { CalendarPlus, Download, CalendarClock, Lock, Video } from "lucide-react";
import { VideoDialog } from "@/components/VideoDialog";
import { InterviewFeedback } from "@/components/InterviewFeedback";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { useLiveEvent } from "@/context/RealtimeContext";
import { PrimaryBtn } from "@/pages/app/Dashboard";
import { Input } from "@/components/ui/input";
import { useApi } from "@/lib/useApi";

const gStamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const gcalUrl = (iv) => `https://calendar.google.com/calendar/render?${new URLSearchParams({
  action: "TEMPLATE", text: iv.title, dates: `${gStamp(iv.start)}/${gStamp(iv.end)}`, details: iv.notes || "AYDORIQ interview", location: iv.location || "",
})}`;

const downloadIcs = async (iv) => {
  try {
    const { data } = await api.get(`/interviews/${iv.id}/ics`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `aydoriq-interview-${iv.id}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

const defaultStart = () => {
  const d = new Date(Date.now() + 86400000);
  d.setMinutes(0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const InterviewPanel = ({ r, onScheduled }) => {
  const { t } = useLang();
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const h = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(h); }, []);
  const joinable = (iv) => r.state === "active" && now >= new Date(iv.start).getTime() - 600000 && now <= new Date(iv.end).getTime() + 1800000;
  const joinVideo = async (iv) => { try { const { data } = await api.post(`/interviews/${iv.id}/join`); setSession(data); } catch (e) { toast.error(errMsg(e)); } };
  const [f, setF] = useState({ start: defaultStart(), duration_min: 45, location: "", notes: "" });
  const allowed = r.state === "active" && r.granted_actions.includes("schedule_interview");
  const { data: list, reload: load } = useApi(`/relationships/${r.id}/interviews`, { initial: [] });
  useLiveEvent(["notification"], (m) => m.data.type === "interview" && m.data.data.relationship_id === r.id && load());

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/relationships/${r.id}/interviews`, { ...f, start: new Date(f.start).toISOString(), duration_min: Number(f.duration_min) });
      toast.success(t("interview_ok"));
      load();
      onScheduled();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="interview-panel">
      <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500"><CalendarClock className="h-4 w-4 text-yellow-300" />{t("interviews")}</p>
      {allowed ? (
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="interview-form">
          <label className="space-y-1 text-xs text-slate-400">{t("start")}<Input type="datetime-local" required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="interview-start-input" /></label>
          <label className="space-y-1 text-xs text-slate-400">{t("duration")}<Input type="number" min={15} max={480} value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: e.target.value })} data-testid="interview-duration-input" /></label>
          <Input className="sm:col-span-2" placeholder={t("meet_location")} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="interview-location-input" />
          <Input className="sm:col-span-2" placeholder={t("notes")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="interview-notes-input" />
          <PrimaryBtn type="submit" className="w-fit" data-testid="schedule-interview-btn"><CalendarPlus className="h-4 w-4" />{t("schedule")}</PrimaryBtn>
        </form>
      ) : (
        <p className="flex items-center gap-2 text-xs text-amber-300" data-testid="interview-locked"><Lock className="h-3.5 w-3.5" />{t("interview_locked")}</p>
      )}
      <ul className="mt-5 space-y-2">
        {list.map((iv) => (
          <li key={iv.id} className="flex flex-wrap items-center justify-between gap-3 rounded border border-white/5 px-3 py-2" data-testid={`interview-item-${iv.id}`}>
            <div>
              <p className="text-sm text-white">{new Date(iv.start).toLocaleString()} · {iv.duration_min} min</p>
              <p className="font-mono text-[10px] text-slate-500">{iv.created_by_name}{iv.location && ` · ${iv.location}`}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {joinable(iv) ? (
                <button onClick={() => joinVideo(iv)} data-testid={`join-video-btn-${iv.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-slate-950 hover:opacity-90"><Video className="h-3.5 w-3.5" />{t("join_video")}</button>
              ) : <span className="inline-flex items-center gap-1 font-mono text-[10px] text-slate-500" data-testid={`video-opens-${iv.id}`}><Video className="h-3 w-3" />{t("video_opens")}</span>}
              <a href={gcalUrl(iv)} target="_blank" rel="noopener noreferrer" data-testid={`gcal-link-${iv.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 px-3 py-1 text-xs text-emerald-300 transition-colors hover:bg-emerald-400 hover:text-slate-950"><CalendarPlus className="h-3.5 w-3.5" />{t("add_gcal")}</a>
              <button onClick={() => downloadIcs(iv)} data-testid={`ics-download-${iv.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-slate-300 hover:border-white/40"><Download className="h-3.5 w-3.5" />{t("download_ics")}</button>
            </div>
            <div className="basis-full"><InterviewFeedback iv={iv} r={r} onSaved={() => { load(); onScheduled(); }} /></div>
          </li>
        ))}
      </ul>
      <VideoDialog session={session} title={t("join_video")} onClose={() => setSession(null)} />
    </div>
  );
};
